import { ClinicSlugSchema, PublicAppointmentTokenSchema } from "@emr/contracts";

export interface PublicAppointmentLink {
  readonly clinicSlug: string;
  readonly purpose: "intake" | "manage";
  readonly token: string | null;
}

export interface PublicDocumentLinkLocation {
  readonly clinicSlug: string;
  readonly purpose: "document";
  readonly token: string | null;
}

export type PublicPatientLink = PublicAppointmentLink | PublicDocumentLinkLocation;

/** `a=1&token=x&b` without its `token` entries, every other entry kept exactly as written. */
function withoutTokenEntries(entries: string): string {
  return entries
    .split("&")
    .filter((entry) => entry !== "token" && !entry.startsWith("token="))
    .join("&");
}

/**
 * Removes any `token` from the address on whatever page it arrives, keeping everything else
 * (such as `?embed=1`), so a link credential never stays in the address bar or history.
 */
export function removeAddressTokens(): void {
  const { pathname, search, hash } = window.location;
  const query = withoutTokenEntries(search.slice(1));
  const fragment = withoutTokenEntries(hash.slice(1));
  if (query === search.slice(1) && fragment === hash.slice(1)) return;
  window.history.replaceState(
    window.history.state,
    "",
    `${pathname}${query ? `?${query}` : ""}${fragment ? `#${fragment}` : ""}`,
  );
}

/**
 * Read once before rendering, and again when the fragment changes; credentials never enter a
 * query string or persistent storage. A secure link's address keeps only its path; any other
 * page loses only its tokens.
 */
export function takePublicPatientLink(): PublicPatientLink | null {
  const match = /^\/(intake|manage|document)\/([^/]+)\/?$/.exec(window.location.pathname);
  if (!match) {
    removeAddressTokens();
    return null;
  }
  const slug = ClinicSlugSchema.safeParse(match[2]);
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const token = PublicAppointmentTokenSchema.safeParse(fragment.get("token"));
  window.history.replaceState(null, "", window.location.pathname);
  if (!slug.success) return null;
  return {
    clinicSlug: slug.data,
    purpose: match[1] === "document" ? "document" : match[1] === "manage" ? "manage" : "intake",
    token: token.success ? token.data : null,
  };
}
